import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Settings, Coins, HardDrive, AlertTriangle, Save, Undo, Eye, Plus, Minus } from "lucide-react";

interface UserBalanceDetails {
  user: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
    subscriptionStatus: string;
    createdAt: string;
  };
  balances: {
    illustrationCredits: number;
    extraStorageGb: number;
    hasUnlimitedStorage: boolean;
    lastUpdated: string | null;
  };
  currentUsage: {
    baseIllustrationsUsed: number;
    storageUsedBytes: number;
    storageUsedGb: number;
    periodStart: string | null;
    periodEnd: string | null;
  };
  subscriptionAllowances: {
    baseIllustrations: number;
    baseStorageGb: number;
  };
}

interface BalanceAdjustment {
  illustrations?: number;
  storageGb?: number;
  unlimited?: boolean;
  reason: string;
}

interface AdminBalanceAdjustmentProps {
  userId: number;
  onClose?: () => void;
  onSuccess?: () => void;
}

export default function AdminBalanceAdjustment({ userId, onClose, onSuccess }: AdminBalanceAdjustmentProps) {
  const { toast } = useToast();
  const [adjustments, setAdjustments] = useState<BalanceAdjustment>({
    illustrations: 0,
    storageGb: 0,
    unlimited: undefined,
    reason: ""
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Fetch user balance details
  const { data: userResponse, isLoading, error, refetch } = useQuery<{
    success: boolean;
    data: {
      users: UserBalanceDetails[];
    };
  }>({
    queryKey: ["/api/admin/addons/users", { 
      limit: 1,
      offset: 0,
      search: userId.toString()
    }],
    staleTime: 30 * 1000, // 30 seconds
  });

  const userDetails = userResponse?.data?.users?.[0];

  // Adjust balance mutation
  const adjustMutation = useMutation({
    mutationFn: async (adjustment: BalanceAdjustment) => {
      const response = await apiRequest("POST", `/api/admin/addons/users/${userId}/adjust`, adjustment);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to adjust balance");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/addons/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/addons/transactions"] });
      toast({
        title: "Balance Adjusted",
        description: "User's add-on balances have been successfully updated.",
      });
      setConfirmDialogOpen(false);
      resetForm();
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Adjustment Failed",
        description: `Failed to adjust balance: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setAdjustments({
      illustrations: 0,
      storageGb: 0,
      unlimited: undefined,
      reason: ""
    });
    setShowPreview(false);
  };

  const handleAdjustmentChange = (field: keyof BalanceAdjustment, value: any) => {
    setAdjustments(prev => ({
      ...prev,
      [field]: value
    }));
    setShowPreview(true);
  };

  const validateAdjustments = () => {
    if (!adjustments.reason.trim()) {
      return "Reason is required for all balance adjustments";
    }
    
    if (adjustments.reason.trim().length < 10) {
      return "Reason must be at least 10 characters long";
    }

    const hasChanges = 
      (adjustments.illustrations && adjustments.illustrations !== 0) ||
      (adjustments.storageGb && adjustments.storageGb !== 0) ||
      (adjustments.unlimited !== undefined);

    if (!hasChanges) {
      return "At least one balance adjustment is required";
    }

    return null;
  };

  const getPreviewBalances = () => {
    if (!userDetails) return null;

    return {
      illustrations: userDetails.balances.illustrationCredits + (adjustments.illustrations || 0),
      storageGb: userDetails.balances.extraStorageGb + (adjustments.storageGb || 0),
      unlimited: adjustments.unlimited !== undefined ? adjustments.unlimited : userDetails.balances.hasUnlimitedStorage
    };
  };

  const handleSubmit = () => {
    const validation = validateAdjustments();
    if (validation) {
      toast({
        title: "Validation Error",
        description: validation,
        variant: "destructive",
      });
      return;
    }

    setConfirmDialogOpen(true);
  };

  const confirmAdjustment = () => {
    const adjustmentData: BalanceAdjustment = {
      reason: adjustments.reason.trim()
    };

    if (adjustments.illustrations && adjustments.illustrations !== 0) {
      adjustmentData.illustrations = adjustments.illustrations;
    }
    if (adjustments.storageGb && adjustments.storageGb !== 0) {
      adjustmentData.storageGb = adjustments.storageGb;
    }
    if (adjustments.unlimited !== undefined) {
      adjustmentData.unlimited = adjustments.unlimited;
    }

    adjustMutation.mutate(adjustmentData);
  };

  if (isLoading) {
    return (
      <Card data-testid="admin-balance-adjustment">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Adjust User Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !userDetails) {
    return (
      <Card data-testid="admin-balance-adjustment">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Adjust User Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center text-red-600 py-8">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error ? `Error loading user: ${error instanceof Error ? error.message : "Unknown error"}` : "User not found"}
          </div>
        </CardContent>
      </Card>
    );
  }

  const previewBalances = getPreviewBalances();
  const validationError = validateAdjustments();

  return (
    <div className="space-y-6" data-testid="admin-balance-adjustment">
      {/* User Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            User Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">User Details</Label>
              <div className="mt-2 space-y-1">
                <div className="font-medium">{userDetails.user.email}</div>
                {(userDetails.user.firstName || userDetails.user.lastName) && (
                  <div className="text-sm text-muted-foreground">
                    {userDetails.user.firstName} {userDetails.user.lastName}
                  </div>
                )}
                <div className="text-xs text-muted-foreground">ID: {userDetails.user.id}</div>
                <Badge variant="outline" className="mt-2">
                  {userDetails.user.subscriptionStatus}
                </Badge>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Current Usage Period</Label>
              <div className="mt-2 space-y-1 text-sm">
                <div>Start: {userDetails.currentUsage.periodStart ? 
                  new Date(userDetails.currentUsage.periodStart).toLocaleDateString() : 'N/A'}</div>
                <div>End: {userDetails.currentUsage.periodEnd ? 
                  new Date(userDetails.currentUsage.periodEnd).toLocaleDateString() : 'N/A'}</div>
                <div className="text-muted-foreground">
                  Base illustrations used: {userDetails.currentUsage.baseIllustrationsUsed}/{userDetails.subscriptionAllowances.baseIllustrations}
                </div>
                <div className="text-muted-foreground">
                  Storage used: {userDetails.currentUsage.storageUsedGb}GB
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Balances */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Current Balances
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-4 border rounded-lg">
              <Coins className="h-8 w-8 text-purple-600" />
              <div>
                <div className="text-2xl font-bold">{userDetails.balances.illustrationCredits}</div>
                <div className="text-sm text-muted-foreground">Illustration Credits</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 border rounded-lg">
              <HardDrive className="h-8 w-8 text-teal-600" />
              <div>
                <div className="text-2xl font-bold">{userDetails.balances.extraStorageGb}GB</div>
                <div className="text-sm text-muted-foreground">Extra Storage</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 border rounded-lg">
              <HardDrive className="h-8 w-8 text-blue-600" />
              <div>
                <div className="text-2xl font-bold">
                  {userDetails.balances.hasUnlimitedStorage ? "YES" : "NO"}
                </div>
                <div className="text-sm text-muted-foreground">Unlimited Storage</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Adjustment Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Balance Adjustments
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Illustration Credits Adjustment */}
          <div className="space-y-3">
            <Label htmlFor="illustrations" className="text-sm font-medium">
              Illustration Credits Adjustment
            </Label>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAdjustmentChange('illustrations', (adjustments.illustrations || 0) - 1)}
                data-testid="decrease-illustrations-button"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                id="illustrations"
                type="number"
                value={adjustments.illustrations || ""}
                onChange={(e) => handleAdjustmentChange('illustrations', parseInt(e.target.value) || 0)}
                placeholder="0"
                className="w-32 text-center"
                data-testid="illustrations-adjustment-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAdjustmentChange('illustrations', (adjustments.illustrations || 0) + 1)}
                data-testid="increase-illustrations-button"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Enter positive numbers to add credits, negative to subtract. Current: {userDetails.balances.illustrationCredits}
            </p>
          </div>

          <Separator />

          {/* Storage GB Adjustment */}
          <div className="space-y-3">
            <Label htmlFor="storageGb" className="text-sm font-medium">
              Extra Storage (GB) Adjustment
            </Label>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAdjustmentChange('storageGb', (adjustments.storageGb || 0) - 1)}
                data-testid="decrease-storage-button"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                id="storageGb"
                type="number"
                step="0.1"
                value={adjustments.storageGb || ""}
                onChange={(e) => handleAdjustmentChange('storageGb', parseFloat(e.target.value) || 0)}
                placeholder="0"
                className="w-32 text-center"
                data-testid="storage-adjustment-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAdjustmentChange('storageGb', (adjustments.storageGb || 0) + 1)}
                data-testid="increase-storage-button"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Enter positive numbers to add storage, negative to subtract. Current: {userDetails.balances.extraStorageGb}GB
            </p>
          </div>

          <Separator />

          {/* Unlimited Storage Toggle */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Unlimited Storage</Label>
            <div className="flex items-center gap-4">
              <div className="text-sm">
                Current: {userDetails.balances.hasUnlimitedStorage ? "Enabled" : "Disabled"}
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="unlimited-toggle" className="text-sm">Change to:</Label>
                <div className="flex gap-2">
                  <Button
                    variant={adjustments.unlimited === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleAdjustmentChange('unlimited', true)}
                    data-testid="enable-unlimited-button"
                  >
                    Enable
                  </Button>
                  <Button
                    variant={adjustments.unlimited === false ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleAdjustmentChange('unlimited', false)}
                    data-testid="disable-unlimited-button"
                  >
                    Disable
                  </Button>
                  <Button
                    variant={adjustments.unlimited === undefined ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleAdjustmentChange('unlimited', undefined)}
                    data-testid="no-change-unlimited-button"
                  >
                    No Change
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Reason for Adjustment */}
          <div className="space-y-3">
            <Label htmlFor="reason" className="text-sm font-medium">
              Reason for Adjustment <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="reason"
              value={adjustments.reason}
              onChange={(e) => handleAdjustmentChange('reason', e.target.value)}
              placeholder="Enter a detailed reason for this balance adjustment (required for audit trail)..."
              className="min-h-[100px]"
              data-testid="adjustment-reason-textarea"
            />
            <p className="text-xs text-muted-foreground">
              This reason will be logged for audit purposes. Minimum 10 characters required.
            </p>
          </div>

          {/* Preview Section */}
          {showPreview && previewBalances && (
            <>
              <Separator />
              <div className="space-y-3">
                <Label className="text-sm font-medium">Preview Changes</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="text-center">
                    <div className="text-lg font-bold">
                      {userDetails.balances.illustrationCredits} → {Math.max(0, previewBalances.illustrations)}
                    </div>
                    <div className="text-sm text-muted-foreground">Illustration Credits</div>
                    {adjustments.illustrations !== 0 && (
                      <div className={`text-xs ${adjustments.illustrations! > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {adjustments.illustrations! > 0 ? '+' : ''}{adjustments.illustrations}
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold">
                      {userDetails.balances.extraStorageGb}GB → {Math.max(0, previewBalances.storageGb)}GB
                    </div>
                    <div className="text-sm text-muted-foreground">Extra Storage</div>
                    {adjustments.storageGb !== 0 && (
                      <div className={`text-xs ${adjustments.storageGb! > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {adjustments.storageGb! > 0 ? '+' : ''}{adjustments.storageGb}GB
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold">
                      {userDetails.balances.hasUnlimitedStorage ? "YES" : "NO"} → {previewBalances.unlimited ? "YES" : "NO"}
                    </div>
                    <div className="text-sm text-muted-foreground">Unlimited Storage</div>
                    {adjustments.unlimited !== undefined && adjustments.unlimited !== userDetails.balances.hasUnlimitedStorage && (
                      <div className={`text-xs ${adjustments.unlimited ? 'text-green-600' : 'text-red-600'}`}>
                        {adjustments.unlimited ? 'Enabling' : 'Disabling'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={resetForm}
                disabled={adjustMutation.isPending}
                data-testid="reset-form-button"
              >
                <Undo className="h-4 w-4 mr-2" />
                Reset
              </Button>
              {onClose && (
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={adjustMutation.isPending}
                  data-testid="cancel-adjustment-button"
                >
                  Cancel
                </Button>
              )}
            </div>
            <Button
              onClick={handleSubmit}
              disabled={!!validationError || adjustMutation.isPending}
              data-testid="submit-adjustment-button"
            >
              <Save className="h-4 w-4 mr-2" />
              {adjustMutation.isPending ? "Adjusting..." : "Apply Adjustment"}
            </Button>
          </div>

          {validationError && (
            <div className="text-sm text-red-600 mt-2">
              {validationError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent data-testid="confirm-adjustment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Balance Adjustment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to apply these balance adjustments for {userDetails.user.email}?
              
              {previewBalances && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium mb-2">Changes to be applied:</div>
                  <ul className="text-sm space-y-1">
                    {adjustments.illustrations !== 0 && (
                      <li>• Illustration Credits: {userDetails.balances.illustrationCredits} → {Math.max(0, previewBalances.illustrations)} ({adjustments.illustrations! > 0 ? '+' : ''}{adjustments.illustrations})</li>
                    )}
                    {adjustments.storageGb !== 0 && (
                      <li>• Extra Storage: {userDetails.balances.extraStorageGb}GB → {Math.max(0, previewBalances.storageGb)}GB ({adjustments.storageGb! > 0 ? '+' : ''}{adjustments.storageGb}GB)</li>
                    )}
                    {adjustments.unlimited !== undefined && adjustments.unlimited !== userDetails.balances.hasUnlimitedStorage && (
                      <li>• Unlimited Storage: {userDetails.balances.hasUnlimitedStorage ? "Enabled" : "Disabled"} → {previewBalances.unlimited ? "Enabled" : "Disabled"}</li>
                    )}
                  </ul>
                  <div className="mt-3 text-sm">
                    <strong>Reason:</strong> {adjustments.reason}
                  </div>
                </div>
              )}
              
              <div className="mt-4 text-sm text-orange-600">
                This action will be logged for audit purposes and cannot be undone.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-confirm-button">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmAdjustment}
              disabled={adjustMutation.isPending}
              data-testid="confirm-adjustment-button"
            >
              {adjustMutation.isPending ? "Applying..." : "Confirm Adjustment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}